<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import _ from 'lodash'
import { ref, onMounted, getCurrentInstance, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import IconBase from '../icon/IconBase.vue'
import IconCross from '../icon/IconCross.vue'
import IconPlus from '../icon/IconPlus.vue'
import FormButton from '../Atoms/FormButton.vue'
import StreamService from '@/services/StreamService'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import FormInput from '../Atoms/FormInput.vue'
import FormInputDuration from '../Atoms/FormInputDuration.vue'
import { timeStringToObject } from '@/utils'
import { track, EVENTS } from '@/lib/tracking'

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()

const props = defineProps({
  vodFile: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  vodFolder: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id');
    }
  },
  playerUrl: {
    type: String
  }
})

const chapters = ref(null)
const chaptersCopy = ref(null)
const saveDisabled = ref(true)
const saveProcessing = ref(false)
const chapterSelectedIndex = ref(null)

const emit = defineEmits({
  'route-change': (id) => { return id },
  'video-update': () => true,
  'video-removed': () => true
})

onMounted(async () => {
  getChapters()
})

watch(() => `${route.params.streamId}/${route.params.videoId}`, async () => {
  getChapters()
})

const getChapters = async () => {
  const videoChapters = await StreamService.getVideoChapters(route.params.streamId, props.vodFile?._id)
  chapters.value = _.map(videoChapters, (item) => {
    item.startSec = formatTime(item.startSec)
    item.endSec = formatTime(item.endSec)
    return item
  })
  copyChapters()
  updateSaveDisabled()
}
const formatTime = (seconds) => {
  const secNum = parseInt(seconds, 10)
  const hours = Math.floor(secNum / 3600)
  const minutes = Math.floor(secNum / 60) % 60
  const sec = secNum % 60

  return { hours, minutes, seconds: sec }
}

const updateChapterStart = (value, item) => {
  const startSec = timeStringToObject(value).hours * 60 * 60 + timeStringToObject(value).minutes * 60 + timeStringToObject(value).seconds
  const endSec = item.endSec.hours * 60 * 60 + item.endSec.minutes * 60 + item.endSec.seconds
  item.startSec = timeStringToObject(value)
  if (endSec <= startSec) {
    item.endSec = item.startSec
  }
  onVodChapterChange(item)
}

const updateChapterEnd = (value, item) => {
  console.log(item);
  console.log(timeStringToObject(value));
  const startSec = item.startSec.hours * 60 * 60 + item.startSec.minutes * 60 + item.startSec.seconds
  const endSec = timeStringToObject(value).hours * 60 * 60 + timeStringToObject(value).minutes * 60 + timeStringToObject(value).seconds
  const duration = props.vodFile?.mediaInfo?.duration
  endSec <= startSec ? item.errorMsg = 'Invalid time range selected' : item.errorMsg = false

  const hasTimeDurationError = duration && ((startSec > duration) || (endSec > duration))
  item.endSec = timeStringToObject(value)
  let isChapterNameEmpty = item.chapter == ''
  if (hasTimeDurationError) {
    item.errorMsg = 'selected time greater than video duration'
  } else if (isChapterNameEmpty) {
    item.errorMsg = 'Chapter field required'
  } else {
    item.errorMsg = false
  }
  onVodChapterChange(item)
}

const hasValidationErrors = () => {
  if (!chapters.value?.length) return true
  return chapters.value.some((item) => !String(item.chapter ?? '').trim() || item.errorMsg)
}

const chaptersDifferFromSaved = () => {
  if (!chaptersCopy.value || !chapters.value) return false
  return !_.isEqual(chapters.value, chaptersCopy.value)
}

const updateSaveDisabled = () => {
  saveDisabled.value = !chaptersDifferFromSaved() || hasValidationErrors()
}

const onVodChapterChange = (item) => {
  item?.chapter?.trim() ? item.errorMsg = false : item.errorMsg = 'Chapter field required'
  updateSaveDisabled()
}

watch(chapters, () => {
  chapters.value?.forEach((item) => {
    if (item.chapter?.trim() && item.errorMsg === 'Chapter field required') {
      item.errorMsg = false
    }
  })
  updateSaveDisabled()
}, { deep: true })

const removeChapter = (index) => {
  chapterSelectedIndex.value = index
  showModal('confirm-chapter-removal');
}

const preventEnter = (e) => {
  e.preventDefault()
}
const addChapter = async () => {
  let startSec = {
    hours: 0,
    minutes: 0,
    seconds: 0
  }
  let endSec = {
    hours: 0,
    minutes: 0,
    seconds: 1
  }
  if (chapters.value.length > 0) {
    startSec = chapters.value[chapters.value.length - 1].endSec
    endSec = chapters.value[chapters.value.length - 1].endSec
  }

  const chapter = {
    startSec,
    endSec,
    chapter: '',
    errorMsg: false,
    modified: false,
    orphan: true
  }
  chapters.value.push(chapter)
  copyChapters()
  updateSaveDisabled()
}

const copyChapters = () => {
  chaptersCopy.value = _.cloneDeep(chapters.value);
}

const saveChapters = async () => {
  saveProcessing.value = true
  try {
    const data = _.map(chapters.value, (item) => {
      return {
        startSec: item.startSec.hours * 60 * 60 + item.startSec.minutes * 60 + item.startSec.seconds,
        endSec: item.endSec.hours * 60 * 60 + item.endSec.minutes * 60 + item.endSec.seconds,
        chapter: item.chapter
      }
    })

    const resp = await StreamService.addVideoChapters(route.params.streamId, props.vodFile._id, data)

    chapters.value = _.map(resp.chapters, (item) => {
      item.startSec = formatTime(item.startSec)
      item.endSec = formatTime(item.endSec)
      return item
    })

    copyChapters()
    updateSaveDisabled()

    track(EVENTS.CHAPTERS_ADDED, {
      chapterCount: data.length,
      folderId: props.vodFolder?._id,
      fileId: props.vodFile?._id,
    })
    notify({ group: 'success', text: 'Chapters added' })
  } catch (err) {
    console.log(err)
    notify({ group: 'error', text: 'Unable to add chapters.' })
  }
  saveProcessing.value = false
}

const removeVideoChapter = async () => {
  const chapter = chapters.value[chapterSelectedIndex.value]

  if (!chapter.id) {
    chapters.value.splice(chapterSelectedIndex.value, 1);
    copyChapters()
    updateSaveDisabled()
    return
  }

  try {
    await StreamService.deleteVideoChapter(route.params.streamId,props.vodFile._id, chapter.id)
    chapters.value.splice(chapterSelectedIndex.value, 1)
    notify({ group: 'success', text: 'Chapter deleted' })
    copyChapters()
    updateSaveDisabled()
  } catch (err) {
    console.log(err)
  }
}
</script>

<template>
  <section>
    <div class="flex items-start justify-between gap-3 mb-4">
      <div class="min-w-0">
        <h3 class="text-[15px] font-medium text-white flex items-center gap-2">
          Chapters
          <span v-if="chapters && chapters.length"
            class="text-xs font-medium text-surface-9 bg-surface-3 rounded-full min-w-5 text-center px-1.5 py-0.5">{{ chapters.length }}</span>
        </h3>
        <p class="text-xs text-surface-8 mt-0.5">Let viewers jump to key moments from the player timeline.</p>
      </div>
      <FormButton type="main" class="shrink-0" @click="addChapter">
        <icon-base class="!mr-1 shrink-0" fill="transparent">
          <icon-plus />
        </icon-base>
        Add chapter
      </FormButton>
    </div>

    <!-- Empty state -->
    <div v-if="!chapters || chapters.length === 0"
      class="rounded-lg bg-surface-1 py-12 px-4 text-center flex flex-col items-center gap-3">
      <span class="size-10 grid place-items-center rounded-full bg-surface-3 text-surface-8">
        <icon-base class="shrink-0" fill="transparent" width-view-box="24" height-view-box="24">
          <icon-plus />
        </icon-base>
      </span>
      <div>
        <p class="text-white font-medium !mb-1">No chapters yet</p>
        <p class="text-surface-8 text-sm">Break the video into sections so viewers can skip to what matters.</p>
      </div>
      <FormButton type="secondary" @click="addChapter">Add your first chapter</FormButton>
    </div>

    <!-- Chapter list — one card per chapter -->
    <ul v-else class="flex flex-col gap-y-2">
      <li v-for="(chapter, index) in chapters" :key="index"
        class="rounded-lg bg-surface-1 p-3 flex flex-col gap-2">
        <!-- Title row -->
        <div class="flex items-center gap-2">
          <span class="shrink-0 size-6 grid place-items-center rounded-md bg-surface-3 text-xs font-medium text-surface-9 tabular-nums">{{ index + 1 }}</span>
          <FormInput class="flex-1" v-model="chapter.chapter" placeholder="Chapter title"
            @keydown.enter.prevent="preventEnter" />
          <FormButton title="Remove chapter"
            custom-classes="shrink-0 size-8 grid place-items-center rounded-md text-surface-8 hover:text-error hover:bg-surface-3 transition-colors"
            @click="removeChapter(index)">
            <icon-base class="shrink-0" fill="transparent">
              <icon-cross />
            </icon-base>
          </FormButton>
        </div>
        <!-- Time range row -->
        <div class="flex items-end gap-3 pl-8">
          <div class="min-w-0">
            <label class="block text-tiny text-surface-8 mb-1">Start</label>
            <FormInputDuration is-object format="HH:mm:ss" :modelValue="chapter.startSec"
              @update:modelValue="value => updateChapterStart(value, chapter)" />
          </div>
          <span class="text-surface-7 pb-2">–</span>
          <div class="min-w-0">
            <label class="block text-tiny text-surface-8 mb-1">End</label>
            <FormInputDuration is-object format="HH:mm:ss" :modelValue="chapter.endSec"
              @update:modelValue="value => updateChapterEnd(value, chapter)" />
          </div>
        </div>
        <span v-if="chapter.errorMsg" class="text-error text-tiny pl-8">{{ chapter?.errorMsg }}</span>
      </li>
    </ul>

    <div v-if="chapters && chapters.length" class="flex justify-end mt-4">
      <FormButton type="main" :isLoading="saveProcessing" :disabled="saveDisabled" @click="saveChapters">
        Save changes
      </FormButton>
    </div>

    <confirm-modal :message="'You are about to delete chapter'" modal-type="danger" modal-id="confirm-chapter-removal"
      ok-text="Delete" cancel-text="Keep" @modal-confirm="removeVideoChapter()" />
  </section>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
